import { alpha, Button, Stack } from "@mui/material";

export const WEARABLE_RANGE_PRESETS = [1, 5, 10, 20, 30] as const;
export type WearableRangeDays = (typeof WEARABLE_RANGE_PRESETS)[number];

/** Presets do dashboard fitness do paciente (sem 10 dias). */
export const FITNESS_RANGE_PRESETS = [1, 5, 20, 30] as const;
export type FitnessRangeDays = (typeof FITNESS_RANGE_PRESETS)[number];

export function isValidFitnessRangeDays(value: number): value is FitnessRangeDays {
  return (FITNESS_RANGE_PRESETS as readonly number[]).includes(value);
}

export function isValidWearableRangeDays(value: number): value is WearableRangeDays {
  return (WEARABLE_RANGE_PRESETS as readonly number[]).includes(value);
}

/** Fallback para valores legados (ex.: preset 15 removido). */
export function normalizeWearableRangeDays(value: number): WearableRangeDays {
  if (isValidWearableRangeDays(value)) return value;
  if (value === 15) return 20;
  return 10;
}

function formatRangePresetLabel(preset: WearableRangeDays): string {
  return preset === 1 ? "1 dia" : `${preset} dias`;
}

interface WearableDateRangeChipsProps {
  rangeDays: number;
  onChange: (days: WearableRangeDays) => void;
  disabled?: boolean;
  tone?: "default" | "onPrimary";
  /** Lista de presets; padrão = WEARABLE_RANGE_PRESETS (atendimento). */
  presets?: readonly number[];
}

export function WearableDateRangeChips({
  rangeDays,
  onChange,
  disabled = false,
  tone = "default",
  presets = WEARABLE_RANGE_PRESETS,
}: WearableDateRangeChipsProps) {
  const isOnPrimary = tone === "onPrimary";

  return (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="nowrap"
      useFlexGap
      justifyContent="center"
      sx={{ width: isOnPrimary ? { xs: "100%", sm: "auto" } : "auto" }}
    >
      {presets.map((preset) => {
        const isActive = rangeDays === preset;
        const presetDays = preset as WearableRangeDays;

        return (
          <Button
            key={preset}
            size="small"
            variant={isOnPrimary ? "text" : isActive ? "contained" : "outlined"}
            color={isOnPrimary ? "inherit" : "primary"}
            disabled={disabled}
            onClick={() => onChange(presetDays)}
            sx={{
              minWidth: "auto",
              px: 1.25,
              flexShrink: 0,
              ...(isOnPrimary && {
                flex: { xs: 1, sm: "0 0 auto" },
              }),
              fontWeight: isActive ? 700 : 500,
              fontSize: "0.8125rem",
              whiteSpace: "nowrap",
              ...(isOnPrimary && {
                color: "white",
                border: "1px solid",
                borderColor: isActive
                  ? (t) => alpha(t.palette.common.white, 0.85)
                  : (t) => alpha(t.palette.common.white, 0.35),
                bgcolor: isActive
                  ? (t) => alpha(t.palette.common.white, 0.22)
                  : "transparent",
                "&:hover": {
                  bgcolor: isActive
                    ? (t) => alpha(t.palette.common.white, 0.28)
                    : (t) => alpha(t.palette.common.white, 0.1),
                  borderColor: (t) => alpha(t.palette.common.white, 0.55),
                },
                "&.Mui-disabled": {
                  color: (t) => alpha(t.palette.common.white, 0.35),
                  borderColor: (t) => alpha(t.palette.common.white, 0.15),
                },
              }),
            }}
          >
            {formatRangePresetLabel(preset)}
          </Button>
        );
      })}
    </Stack>
  );
}
